/**
 * Property Dashboard API Hooks
 * React Query hooks for property dashboard data
 */

import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import AxiosClient from '@/api/client';
import type { ListResponse } from '@/types/api';

// ============================================================================
// Types
// ============================================================================

interface PropertyMetrics {
  property_id: number;
  property_name: string;
  current_score: number | null;
  score_trend: number | null;
  score_trend_text: string | null;
  total_assessments: number;
  assessments_this_month: number;
  departments_count: number;
  last_assessment_date: string | null;
  last_assessment_date_formatted: string | null;
}

interface Department {
  id: string;
  name: string;
  last_score: number;
  touchpoints: number;
  total_standards: number;
  completed_assessments: number;
  pending_assessments: number;
  status: 'active' | 'inactive';
  last_assessment: string | null;
  [key: string]: unknown;
}

interface Assessment {
  id: string;
  date: string;
  auditor: string;
  auditor_email: string;
  department: string;
  score: number;
  status: 'completed' | 'in_progress';
  mid_standard_percentage: number;
  completion_percentage: number;
  [key: string]: unknown;
}

interface OverviewData {
  overall_score: number;
  department_scores: Array<{
    id: string;
    name: string;
    score: number;
    last_assessment_date: string;
  }>;
  trend_data: Array<{
    month: string;
    month_full: string;
    score: number;
  }>;
  summary_stats: {
    highest_score: number;
    highest_department: string | null;
    lowest_score: number;
    lowest_department: string | null;
    score_range: number;
  };
  filtered: boolean;
  date_from: string | null;
}

interface ContactData {
  property_id: number;
  property_name: string;
  contact_person: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  location: string | null;
}

interface ApiPagination {
  count: number;
  next: number | null;
  previous: number | null;
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  from: number | null;
  to: number | null;
  has_more_pages: boolean;
}

// ============================================================================
// Query Keys
// ============================================================================

export const propertyDashboardQueryKeys = {
  all: (propertyId: string | number) =>
    ['property-dashboard', propertyId] as const,
  metrics: (propertyId: string | number) =>
    [...propertyDashboardQueryKeys.all(propertyId), 'metrics'] as const,
  overview: (propertyId: string | number, dateFrom?: string) =>
    [
      ...propertyDashboardQueryKeys.all(propertyId),
      'overview',
      { dateFrom },
    ] as const,
  assessments: (propertyId: string | number, page: number, pageSize: number) =>
    [
      ...propertyDashboardQueryKeys.all(propertyId),
      'assessments',
      { page, pageSize },
    ] as const,
  departments: (
    propertyId: string | number,
    params: {
      page: number;
      pageSize: number;
      search?: string;
      dateFrom?: string;
    }
  ) =>
    [
      ...propertyDashboardQueryKeys.all(propertyId),
      'departments',
      params,
    ] as const,
  contact: (propertyId: string | number) =>
    [...propertyDashboardQueryKeys.all(propertyId), 'contact'] as const,
} as const;

// ============================================================================
// Metrics Query
// ============================================================================

export function usePropertyMetricsQuery(propertyId: string | number) {
  return useQuery({
    queryKey: propertyDashboardQueryKeys.metrics(propertyId),
    queryFn: async (): Promise<PropertyMetrics> => {
      const client = new AxiosClient<undefined, PropertyMetrics>({
        endpoint: `/dashboard/properties/${propertyId}/metrics`,
        isToken: true,
      });

      const response = await client.getItem();

      if (!response.status) {
        throw new Error(response.error || 'Failed to fetch property metrics');
      }

      return response.data;
    },
    enabled: !!propertyId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      if (error instanceof AxiosError && error.response?.status === 401) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

// ============================================================================
// Overview Query
// ============================================================================

export function usePropertyOverviewQuery(
  propertyId: string | number,
  dateFrom?: string
) {
  return useQuery({
    queryKey: propertyDashboardQueryKeys.overview(propertyId, dateFrom),
    queryFn: async (): Promise<OverviewData> => {
      const client = new AxiosClient<undefined, OverviewData>({
        endpoint: `/dashboard/properties/${propertyId}/overview`,
        isToken: true,
      });

      const params = dateFrom ? { date_from: dateFrom } : {};
      const response = await client.getItem({ params });

      if (!response.status) {
        throw new Error(response.error || 'Failed to fetch overview data');
      }

      return response.data;
    },
    enabled: !!propertyId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      if (error instanceof AxiosError && error.response?.status === 401) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

// ============================================================================
// Assessments Query
// ============================================================================

export function usePropertyAssessmentsQuery(
  propertyId: string | number,
  page: number = 1,
  pageSize: number = 10
) {
  return useQuery({
    queryKey: propertyDashboardQueryKeys.assessments(
      propertyId,
      page,
      pageSize
    ),
    queryFn: async (): Promise<
      ListResponse<Assessment> & { pagination: ApiPagination }
    > => {
      const client = new AxiosClient<undefined, Assessment>({
        endpoint: `/dashboard/properties/${propertyId}/assessments`,
        isToken: true,
      });

      const response = await client.getList({
        params: { page, page_size: pageSize },
      });

      if (!response.status) {
        throw new Error(response.error || 'Failed to fetch assessments');
      }

      // Backend returns ListResponse structure with pagination
      return response as ListResponse<Assessment> & {
        pagination: ApiPagination;
      };
    },
    enabled: !!propertyId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: (failureCount, error) => {
      if (error instanceof AxiosError && error.response?.status === 401) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

// ============================================================================
// Departments Query
// ============================================================================

export function usePropertyDepartmentsQuery(
  propertyId: string | number,
  params: {
    page?: number;
    pageSize?: number;
    search?: string;
    dateFrom?: string;
  } = {}
) {
  const { page = 1, pageSize = 10, search, dateFrom } = params;

  return useQuery({
    queryKey: propertyDashboardQueryKeys.departments(propertyId, {
      page,
      pageSize,
      search,
      dateFrom,
    }),
    queryFn: async (): Promise<
      ListResponse<Department> & {
        pagination: ApiPagination;
        filtered: boolean;
      }
    > => {
      const client = new AxiosClient<undefined, Department>({
        endpoint: `/dashboard/properties/${propertyId}/departments`,
        isToken: true,
      });

      const queryParams: Record<string, unknown> = {
        page,
        page_size: pageSize,
      };

      if (search) queryParams.search = search;
      if (dateFrom) queryParams.date_from = dateFrom;

      const response = await client.getList({ params: queryParams });

      if (!response.status) {
        throw new Error(response.error || 'Failed to fetch departments');
      }

      // Backend returns ListResponse structure with pagination
      return response as ListResponse<Department> & {
        pagination: ApiPagination;
        filtered: boolean;
      };
    },
    enabled: !!propertyId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: (failureCount, error) => {
      if (error instanceof AxiosError && error.response?.status === 401) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

// ============================================================================
// Contact Query
// ============================================================================

export function usePropertyContactQuery(propertyId: string | number) {
  return useQuery({
    queryKey: propertyDashboardQueryKeys.contact(propertyId),
    queryFn: async (): Promise<ContactData> => {
      const client = new AxiosClient<undefined, ContactData>({
        endpoint: `/dashboard/properties/${propertyId}/contact`,
        isToken: true,
      });

      const response = await client.getItem();

      if (!response.status) {
        throw new Error(
          response.error || 'Failed to fetch contact information'
        );
      }

      return response.data;
    },
    enabled: !!propertyId,
    staleTime: 10 * 60 * 1000, // 10 minutes (contact info rarely changes)
    retry: (failureCount, error) => {
      if (error instanceof AxiosError && error.response?.status === 401) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

// ============================================================================
// Export Types
// ============================================================================

export type {
  PropertyMetrics,
  Department,
  Assessment,
  OverviewData,
  ContactData,
  ApiPagination,
};
